import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';

/**
 * PortalDropdown / PortalMenu
 *
 * Two usage modes — both portal-based (document.body) so they NEVER clip inside
 * table overflow or any stacking context.
 *
 * ── Mode 1: items array (canonical PortalMenu) ──────────────────────────────
 *   <PortalDropdown
 *     anchorRef={ref}
 *     open={open}
 *     onClose={close}
 *     items={[
 *       { label: 'Edit',   icon: 'Pencil', onClick: handleEdit },
 *       { label: 'Delete', icon: 'Trash2', onClick: handleDelete, variant: 'danger' },
 *       { label: 'N/A',    icon: 'Lock',   onClick: noop,         disabled: true },
 *     ]}
 *   />
 *
 * ── Mode 2: children (legacy — existing consumers unchanged) ─────────────────
 *   <PortalDropdown anchorRef={ref} open={open} onClose={close} width={160}>
 *     <button ...>...</button>
 *   </PortalDropdown>
 *
 * Props (shared):
 *   anchorRef  — React ref attached to the trigger button
 *   open       — boolean controlling visibility
 *   onClose    — callback fired on outside-click / Esc / scroll
 *   width      — optional pixel width (default: 180 for items mode, 160 for children mode)
 *   align      — 'right' (default) or 'left'
 *
 * Props (items mode only):
 *   items      — array of {
 *                  label, icon?, onClick,
 *                  variant?: 'default'|'danger',
 *                  disabled?,
 *                  dot?    — CSS colour string: coloured dot instead of an icon
 *                  active? — marks the current option (bold row + check on the right)
 *                }
 *
 * Vertical bounding: a menu that would overflow the bottom of the viewport
 * flips above its anchor; if it fits neither way it is clamped inside the
 * viewport. Menus that already fit keep exactly their previous position.
 */
const PortalDropdown = ({
  anchorRef,
  open,
  onClose,
  children,
  items,
  width = 0,
  align = 'right',
}) => {
  const resolvedWidth = width || (items ? 180 : 160);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  // ── Position + vertical bounding ────────────────────────────────────────────
  // useLayoutEffect : le menu est deja monte, on peut mesurer sa hauteur reelle
  // et corriger avant la peinture, donc sans saut visible.
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    const rect = anchorRef?.current?.getBoundingClientRect();
    const left =
      align === 'left'
        ? rect?.left + window.scrollX
        : rect?.right + window.scrollX - resolvedWidth;

    const height = dropdownRef?.current?.offsetHeight || 0;
    const MARGIN = 8;
    let top = rect?.bottom + window.scrollY + 4;

    if (height && rect?.bottom + 4 + height > window.innerHeight - MARGIN) {
      const fitsAbove = rect?.top - 4 - height >= MARGIN;
      top = fitsAbove
        ? rect?.top + window.scrollY - height - 4
        : Math.max(
            window.scrollY + MARGIN,
            window.scrollY + window.innerHeight - height - MARGIN
          );
    }

    setPos((prev) =>
      prev?.top === top && prev?.left === left ? prev : { top, left }
    );
  }, [open, anchorRef, resolvedWidth, align, items, children]);

  // ── Outside-click close ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        !anchorRef?.current?.contains(e?.target) &&
        !dropdownRef?.current?.contains(e?.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, anchorRef]);

  // ── Esc key close ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e?.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // ── Scroll close ────────────────────────────────────────────────────────────
  // Le menu vit dans un portail, a des coordonnees absolues : si la page defile
  // dessous, il se detacherait visuellement de son ancre. On ferme donc — mais
  // PAS quand c'est le menu lui-meme qui defile. La phase de capture reste
  // necessaire pour voir le defilement des conteneurs imbriques ; c'est elle qui
  // faisait aussi remonter le defilement de la liste du menu, et le fermait.
  useEffect(() => {
    if (!open) return;
    const handleScroll = (e) => {
      if (dropdownRef?.current?.contains(e?.target)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, onClose]);

  if (!open) return null;

  // ── Items-array rendering (canonical PortalMenu) ────────────────────────────
  const renderItems = () =>
    items?.map((item, i) => {
      const isDanger = item?.variant === 'danger';
      const isDisabled = !!item?.disabled;

      const baseClass =
        'flex items-center gap-2 w-full px-3 py-2 text-xs transition-colors whitespace-nowrap';
      const stateClass = isDisabled
        ? 'text-gray-300 cursor-not-allowed'
        : isDanger
        ? 'text-red-600 hover:bg-red-50 cursor-pointer' :'text-gray-700 hover:bg-gray-50 cursor-pointer';
      const activeClass = item?.active ? 'bg-gray-50 font-semibold' : '';

      return (
        <button
          key={i}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              onClose();
              item?.onClick?.();
            }
          }}
          className={`${baseClass} ${stateClass} ${activeClass}`}
        >
          {item?.dot && (
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: item?.dot }}
            />
          )}
          {item?.icon && !item?.dot && (
            <Icon
              name={item?.icon}
              size={13}
              className={
                isDisabled
                  ? 'text-gray-300 shrink-0'
                  : isDanger
                  ? 'text-red-400 shrink-0' :'text-gray-400 shrink-0'
              }
            />
          )}
          {item?.label}
          {item?.active && (
            <Icon name="Check" size={11} className="ml-auto text-gray-400 shrink-0" />
          )}
        </button>
      );
    });

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: pos?.top,
        left: pos?.left,
        zIndex: 9999,
        width: `${resolvedWidth}px`,
      }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1"
      onMouseDown={(e) => e?.stopPropagation()}
    >
      {items ? renderItems() : children}
    </div>,
    document.body
  );
};

export default PortalDropdown;
